import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Snackbar,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Autocomplete,
  FormControlLabel,
  Checkbox,
  Pagination,
  Stack,
  Menu
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  AddCircle as AddCircleIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
  MoreVert as MoreVertIcon
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { Product, ProductVariant } from '../../types/product';
import { Category } from '../../types/category';
import { getProducts, getProductById, updateProduct } from '../../services/productService';
import { getCategories, getCategoryById } from '../../services/categoryService';
import CategoryBulkExcelImport from './CategoryBulkExcelImport';

// Interface para el estado del formulario de adición de productos a una categoría
interface ProductToCategory {
  productId: string;
  variantId?: string;
}

const CategoryProducts: React.FC = () => {
  const { categoryId } = useParams<{ categoryId: string }>();
  const navigate = useNavigate();
  
  // Estados
  const [category, setCategory] = useState<Category | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [currentTab, setCurrentTab] = useState<number>(0);
  const [openAddDialog, setOpenAddDialog] = useState<boolean>(false);
  const [openRemoveDialog, setOpenRemoveDialog] = useState<boolean>(false);
  const [selectedProduct, setSelectedProduct] = useState<string>('');
  const [selectedVariant, setSelectedVariant] = useState<string>('');
  const [isDefaultCategory, setIsDefaultCategory] = useState<boolean>(false);
  const [productToRemove, setProductToRemove] = useState<{ productId: string, variantId: string } | null>(null);
  const [availableProducts, setAvailableProducts] = useState<Product[]>([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });
  
  // حالات الصفحات للتبويب الأول (منتجات التصنيف)
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage] = useState<number>(10);
  const [totalItems, setTotalItems] = useState<number>(0);
  
  // حالات البحث في التبويب الأول (منتجات التصنيف)
  const [categorySearchInput, setCategorySearchInput] = useState<string>('');
  const [categorySearchTerm, setCategorySearchTerm] = useState<string>('');
  
  // حالات الصفحات للتبويب الثاني (المنتجات المتاحة) - server-side pagination
  const [availableCurrentPage, setAvailableCurrentPage] = useState<number>(1);
  const [availableItemsPerPage] = useState<number>(10);
  const [availableTotalItems, setAvailableTotalItems] = useState<number>(0);
  const [availableTotalPages, setAvailableTotalPages] = useState<number>(0);
  const [availableSearchTerm, setAvailableSearchTerm] = useState<string>('');
  const [availableSearchInput, setAvailableSearchInput] = useState<string>('');
  const [availableLoading, setAvailableLoading] = useState<boolean>(false);

  // حالات الاستيراد المجمع
  const [openBulkImport, setOpenBulkImport] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // Cargar datos iniciales
  useEffect(() => {
    if (categoryId) {
      fetchCategoryData();
    }
  }, [categoryId]);

  // جلب المنتجات المتاحة عند تغيير الصفحة أو مصطلح البحث
  useEffect(() => {
    if (categoryId) {
      fetchAvailableProducts(availableCurrentPage, availableSearchTerm);
    }
  }, [categoryId, availableCurrentPage, availableSearchTerm]);

  // Obtener datos de la categoría
  const fetchCategoryData = async () => {
    setLoading(true);
    try {
      const categoryData = await getCategoryById(categoryId!);
      setCategory(categoryData);
      
      // Obtener productos que están en esta categoría
      await fetchCategoryProducts();
    } catch (error) {
      console.error('Error al cargar datos de la categoría:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء تحميل بيانات التصنيف',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Obtener productos disponibles para agregar a la categoría - server-side pagination
  const fetchAvailableProducts = async (page: number = 1, search: string = '') => {
    try {
      setAvailableLoading(true);
      const response = await getProducts(page, availableItemsPerPage, search || undefined);
      setAvailableProducts(response.products);
      setAvailableTotalItems(response.pagination?.total || 0);
      setAvailableTotalPages(response.pagination?.pages || 0);
    } catch (error) {
      console.error('Error al cargar productos disponibles:', error);
    } finally {
      setAvailableLoading(false);
    }
  };

  // Obtener productos de la categoría
  const fetchCategoryProducts = async () => {
    try {
      // إجبار تحديث البيانات من الخادم (تجاهل التخزين المؤقت)
      const response = await getProducts(1, 1000, undefined, undefined, true); // forceRefresh = true
      
      // Filtrar los productos que tienen esta categoría
      const categoryProducts = response.products.filter(product =>
        product.variants.some(variant =>
          // البحث في تصنيفات المتغير
          (variant.categories && variant.categories.includes(categoryId!)) ||
          // البحث في تصنيفات الباركودات
          (variant.barcodes && variant.barcodes.some(barcode =>
            barcode.categories && barcode.categories.includes(categoryId!)
          ))
        )
      );
      
      // تحديث العدد الإجمالي
      setTotalItems(categoryProducts.length);
      
      setProducts(categoryProducts);
    } catch (error) {
      console.error('Error al cargar productos de la categoría:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء تحميل منتجات التصنيف',
        severity: 'error'
      });
    }
  };

  // معالجة البحث في منتجات التصنيف
  const handleCategorySearch = () => {
    setCurrentPage(1);
    setCategorySearchTerm(categorySearchInput);
  };

  // معالجة الضغط على Enter في حقل بحث منتجات التصنيف
  const handleCategorySearchKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleCategorySearch();
    }
  };

  // مسح البحث في منتجات التصنيف
  const handleClearCategorySearch = () => {
    setCategorySearchInput('');
    setCategorySearchTerm('');
    setCurrentPage(1);
  };

  // Manejar cambio de pestaña
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
    setCurrentPage(1); // إعادة تعيين الصفحة عند تغيير التبويب
    setAvailableCurrentPage(1); // إعادة تعيين صفحة المنتجات المتاحة
  };

  // تغيير الصفحة للتبويب الأول
  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  };

  // تغيير الصفحة للتبويب الثاني
  const handleAvailablePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setAvailableCurrentPage(page);
  };

  // معالجة البحث في المنتجات المتاحة
  const handleAvailableSearch = () => {
    setAvailableCurrentPage(1);
    setAvailableSearchTerm(availableSearchInput);
  };

  // معالجة الضغط على Enter في حقل البحث
  const handleAvailableSearchKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleAvailableSearch();
    }
  };

  // مسح البحث
  const handleClearAvailableSearch = () => {
    setAvailableSearchInput('');
    setAvailableSearchTerm('');
    setAvailableCurrentPage(1);
  };

  // الحصول على جميع صفوف منتجات التصنيف مع تطبيق فلتر البحث
  const getFilteredCategoryRows = () => {
    const searchLower = categorySearchTerm.toLowerCase().trim();
    
    return products.flatMap(product =>
      product.variants
        .filter(variant =>
          // البحث في تصنيفات المتغير
          (variant.categories?.includes(categoryId!)) ||
          // البحث في تصنيفات الباركودات
          (variant.barcodes && variant.barcodes.some(barcode =>
            barcode.categories && barcode.categories.includes(categoryId!)
          ))
        )
        .filter(variant => {
          // تطبيق فلتر البحث إذا كان موجوداً
          if (!searchLower) return true;
          // البحث في اسم المنتج
          if (product.name.toLowerCase().includes(searchLower)) return true;
          // البحث في الباركود
          if (variant.barcodes?.some(b => b.code.toLowerCase().includes(searchLower))) return true;
          // البحث في خصائص المتغير
          if (Object.values(variant.attributes || {}).some(v => String(v).toLowerCase().includes(searchLower))) return true;
          return false;
        })
        .map(variant => ({ product, variant }))
    );
  };

  // حساب المنتجات للصفحة الحالية
  const getPaginatedProducts = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const allRows = getFilteredCategoryRows();
    return allRows.slice(startIndex, endIndex);
  };

  // حساب عدد الصفحات للتبويب الأول
  const getTotalPages = () => {
    return Math.ceil(getFilteredCategoryRows().length / itemsPerPage);
  };

  // تصدير جدول منتجات التصنيف إلى ملف Excel
  const handleExportToExcel = () => {
    const rows = getFilteredCategoryRows();

    if (rows.length === 0) {
      setSnackbar({
        open: true,
        message: 'لا توجد بيانات لتصديرها',
        severity: 'error'
      });
      return;
    }

    const excelRows = rows.map(({ product, variant }) => {
      const defaultBarcode = variant.barcodes?.find(barcode => barcode.defaultCategory === categoryId);

      return {
        'اسم المنتج': product.name,
        'المتغير': Object.entries(variant.attributes || {})
          .map(([key, value]) => `${key}: ${value}`)
          .join(', '),
        'الباركود': (variant.barcodes || []).map(barcode => barcode.code).join(' | '),
        'الوحدة': variant.unit || '',
        'الحالة': variant.status === 'active'
          ? 'نشط'
          : variant.status === 'inactive'
            ? 'غير نشط'
            : 'نفذت الكمية',
        'التصنيف الافتراضي': defaultBarcode ? 'نعم' : 'لا'
      };
    });

    const headers = [
      'اسم المنتج',
      'المتغير',
      'الباركود',
      'الوحدة',
      'الحالة',
      'التصنيف الافتراضي'
    ];

    const dataRows = excelRows.map(row => [
      row['اسم المنتج'],
      row['المتغير'],
      row['الباركود'],
      row['الوحدة'],
      row['الحالة'],
      row['التصنيف الافتراضي']
    ]);

    // الصف الأول: اسم التصنيف - الصف الثاني: عناوين الأعمدة - ثم البيانات
    const worksheet = XLSX.utils.aoa_to_sheet([
      [`اسم التصنيف: ${category?.name || ''}`],
      headers,
      ...dataRows
    ]);

    // دمج خلية اسم التصنيف على عرض الأعمدة
    worksheet['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: headers.length - 1 } }
    ];

    worksheet['!cols'] = [
      { wch: 40 },
      { wch: 25 },
      { wch: 30 },
      { wch: 12 },
      { wch: 14 },
      { wch: 18 }
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'منتجات التصنيف');

    const safeCategoryName = (category?.name || 'category').replace(/[\\/:*?"<>|]/g, '_');
    const datePart = new Date().toISOString().slice(0, 10);

    XLSX.writeFile(workbook, `منتجات_التصنيف_${safeCategoryName}_${datePart}.xlsx`);

    setSnackbar({
      open: true,
      message: `تم تصدير ${excelRows.length} صف إلى ملف Excel`,
      severity: 'success'
    });
  };

  // المنتجات المتاحة تأتي مباشرة من الخادم (server-side pagination)
  // لا حاجة لتقسيم محلي

  // Abrir diálogo para agregar producto
  const handleOpenAddDialog = () => {
    setSelectedProduct('');
    setSelectedVariant('');
    setIsDefaultCategory(false);
    setOpenAddDialog(true);
  };

  // Cerrar diálogo de agregar producto
  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
  };

  // Manejar selección de producto
  const handleProductChange = (event: SelectChangeEvent) => {
    setSelectedProduct(event.target.value);
    setSelectedVariant('');  // Reset variant selection
  };

  // Manejar selección de variante
  const handleVariantChange = (event: SelectChangeEvent) => {
    setSelectedVariant(event.target.value);
  };

  // Agregar producto a la categoría
  const handleAddProductToCategory = async () => {
    if (!selectedProduct) {
      setSnackbar({
        open: true,
        message: 'الرجاء اختيار منتج',
        severity: 'error'
      });
      return;
    }

    try {
      // Obtener el producto completo
      const product = await getProductById(selectedProduct);
      
      // Si se seleccionó una variante específica
      if (selectedVariant) {
        // Actualizar solo la variante seleccionada
        const updatedVariants = product.variants.map(variant => {
          if (variant._id === selectedVariant) {
            const categories = variant.categories || [];
            // Evitar duplicados
            if (!categories.includes(categoryId!)) {
              // Actualizar también los barcodes de la variante
              const updatedBarcodes = variant.barcodes?.map(barcode => ({
                ...barcode,
                categories: [...(barcode.categories || []), categoryId!],
                defaultCategory: isDefaultCategory ? categoryId! : barcode.defaultCategory
              })) || [];
              
              return {
                ...variant,
                categories: [...categories, categoryId!],
                barcodes: updatedBarcodes
              };
            }
          }
          return variant;
        });
        
        // Actualizar el producto con las variantes actualizadas
        await updateProduct(selectedProduct, {
          ...product,
          variants: updatedVariants
        });
      } else {
        // Si no se seleccionó una variante específica, agregar la categoría a todas las variantes
        const updatedVariants = product.variants.map(variant => {
          const categories = variant.categories || [];
          // Evitar duplicados
          if (!categories.includes(categoryId!)) {
            // Actualizar también los barcodes de cada variante
            const updatedBarcodes = variant.barcodes?.map(barcode => ({
              ...barcode,
              categories: [...(barcode.categories || []), categoryId!],
              defaultCategory: isDefaultCategory ? categoryId! : barcode.defaultCategory
            })) || [];
            
            return {
              ...variant,
              categories: [...categories, categoryId!],
              barcodes: updatedBarcodes
            };
          }
          return variant;
        });
        
        // Actualizar el producto con las variantes actualizadas
        await updateProduct(selectedProduct, {
          ...product,
          variants: updatedVariants
        });
      }

      // Cerrar diálogo y actualizar lista
      setOpenAddDialog(false);
      fetchCategoryProducts();
      
      setSnackbar({
        open: true,
        message: 'تم إضافة المنتج إلى التصنيف بنجاح',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error al agregar producto a la categoría:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء إضافة المنتج إلى التصنيف',
        severity: 'error'
      });
    }
  };

  // Abrir diálogo para confirmar eliminar producto
  const handleConfirmRemove = (productId: string, variantId: string) => {
    setProductToRemove({ productId, variantId });
    setOpenRemoveDialog(true);
  };

  // Cerrar diálogo de confirmar eliminar
  const handleCloseRemoveDialog = () => {
    setOpenRemoveDialog(false);
    setProductToRemove(null);
  };

  // Eliminar producto de la categoría
  const handleRemoveProductFromCategory = async () => {
    if (!productToRemove) return;

    try {
      // Obtener el producto completo
      const product = await getProductById(productToRemove.productId);
      
      // Actualizar la variante para eliminar la categoría
      const updatedVariants = product.variants.map(variant => {
        if (variant._id === productToRemove.variantId) {
          const categories = variant.categories || [];
          
          // تحديث الباركودات أيضاً لحذف التصنيف
          const updatedBarcodes = variant.barcodes?.map(barcode => ({
            ...barcode,
            categories: (barcode.categories || []).filter(catId => catId !== categoryId!),
            // إزالة التصنيف الافتراضي إذا كان هو المحذوف
            defaultCategory: barcode.defaultCategory === categoryId! ? undefined : barcode.defaultCategory
          })) || [];
          
          return {
            ...variant,
            categories: categories.filter(catId => catId !== categoryId!),
            barcodes: updatedBarcodes
          };
        }
        return variant;
      });
      
      // Actualizar el producto con las variantes actualizadas
      await updateProduct(productToRemove.productId, {
        ...product,
        variants: updatedVariants
      });

      // Cerrar diálogo y actualizar lista
      setOpenRemoveDialog(false);
      setCurrentPage(1); // إعادة تعيين الصفحة
      fetchCategoryProducts();
      
      setSnackbar({
        open: true,
        message: 'تم حذف المنتج من التصنيف بنجاح',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error al eliminar producto de la categoría:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء حذف المنتج من التصنيف',
        severity: 'error'
      });
    }
  };

  // تعيين التصنيف كافتراضي لمتغير منتج
  const handleSetAsDefaultCategory = async (productId: string, variantId: string) => {
    try {
      // الحصول على المنتج الكامل
      const product = await getProductById(productId);
      
      // تحديث المتغير لتعيين هذا التصنيف كافتراضي
      const updatedVariants = product.variants.map(variant => {
        if (variant._id === variantId) {
          // تحديث الباركودات لتعيين هذا التصنيف كافتراضي
          const updatedBarcodes = variant.barcodes?.map(barcode => {
            // إذا كان الباركود يحتوي على هذا التصنيف، اجعله افتراضي
            if (barcode.categories?.includes(categoryId!)) {
              return {
                ...barcode,
                defaultCategory: categoryId!
              };
            }
            return barcode;
          }) || [];
          
          return {
            ...variant,
            barcodes: updatedBarcodes
          };
        }
        return variant;
      });
      
      // حفظ التحديثات
      await updateProduct(productId, {
        ...product,
        variants: updatedVariants
      });
      
      // تحديث القائمة
      fetchCategoryProducts();
      
      setSnackbar({
        open: true,
        message: 'تم تعيين التصنيف كافتراضي بنجاح',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error setting default category:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء تعيين التصنيف الافتراضي',
        severity: 'error'
      });
    }
  };

  // إلغاء التصنيف الافتراضي لمتغير منتج
  const handleRemoveDefaultCategory = async (productId: string, variantId: string) => {
    try {
      // الحصول على المنتج الكامل
      const product = await getProductById(productId);
      
      // تحديث المتغير لإلغاء التصنيف الافتراضي
      const updatedVariants = product.variants.map(variant => {
        if (variant._id === variantId) {
          // تحديث الباركودات لإلغاء التصنيف الافتراضي
          const updatedBarcodes = variant.barcodes?.map(barcode => ({
            ...barcode,
            defaultCategory: barcode.defaultCategory === categoryId! ? undefined : barcode.defaultCategory
          })) || [];
          
          return {
            ...variant,
            barcodes: updatedBarcodes
          };
        }
        return variant;
      });
      
      // حفظ التحديثات
      await updateProduct(productId, {
        ...product,
        variants: updatedVariants
      });
      
      // تحديث القائمة
      fetchCategoryProducts();
      
      setSnackbar({
        open: true,
        message: 'تم إلغاء التصنيف الافتراضي بنجاح',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error removing default category:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء إلغاء التصنيف الافتراضي',
        severity: 'error'
      });
    }
  };

  // Cerrar snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header con título y botones */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            onClick={() => navigate('/products/categories')}
            sx={{ mr: 1 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" component="h1">
            {loading ? 'جاري التحميل...' : `منتجات التصنيف: ${category?.name || ''}`}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenAddDialog}
          >
            إضافة منتج
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<UploadIcon />}
            onClick={() => setOpenBulkImport(true)}
          >
            استيراد مجمع
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<DownloadIcon />}
            onClick={handleExportToExcel}
          >
            تصدير Excel
          </Button>
          <IconButton
            onClick={(event) => setAnchorEl(event.currentTarget)}
            size="small"
          >
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
          >
            <MenuItem onClick={() => {
              setOpenBulkImport(true);
              setAnchorEl(null);
            }}>
              <UploadIcon sx={{ mr: 1 }} />
              استيراد من الإكسل
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {/* Descripción de la categoría si existe */}
      {category?.description && (
        <Paper sx={{ p: 2, mb: 3, backgroundColor: 'background.default' }}>
          <Typography variant="body1">
            {category.description}
          </Typography>
        </Paper>
      )}

      {/* Contenido principal */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Pestañas para diferentes vistas */}
          <Tabs value={currentTab} onChange={handleTabChange} sx={{ mb: 2 }}>
            <Tab label="منتجات التصنيف" />
            <Tab label="المنتجات المتاحة" />
          </Tabs>

          {/* Vista de productos en la categoría */}
          {currentTab === 0 && (
            <>
              {/* فلتر البحث في منتجات التصنيف */}
              {products.length > 0 && (
                <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
                  <TextField
                    label="بحث في منتجات التصنيف"
                    variant="outlined"
                    size="small"
                    value={categorySearchInput}
                    onChange={(e) => setCategorySearchInput(e.target.value)}
                    onKeyDown={handleCategorySearchKeyDown}
                    sx={{ flexGrow: 1 }}
                    placeholder="اكتب اسم المنتج أو الباركود ثم اضغط بحث أو Enter"
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleCategorySearch}
                  >
                    بحث
                  </Button>
                  {categorySearchTerm && (
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={handleClearCategorySearch}
                    >
                      مسح
                    </Button>
                  )}
                </Box>
              )}

            {products.length === 0 ? (
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  لا توجد منتجات في هذا التصنيف
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddCircleIcon />}
                  onClick={handleOpenAddDialog}
                  sx={{ mt: 2 }}
                >
                  إضافة منتج
                </Button>
              </Paper>
            ) : getFilteredCategoryRows().length === 0 ? (
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  لا توجد منتجات تطابق البحث
                </Typography>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleClearCategorySearch}
                  sx={{ mt: 2 }}
                >
                  مسح البحث
                </Button>
              </Paper>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'primary.main' }}>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>اسم المنتج</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>المتغير</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>الباركود</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>الوحدة</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>الحالة</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">التصنيف الافتراضي</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">إجراءات</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getPaginatedProducts().map(({ product, variant }) => {
                          // البحث عن الباركود الذي له هذا التصنيف كافتراضي
                          const defaultBarcode = variant.barcodes?.find(barcode =>
                            barcode.defaultCategory === categoryId
                          );
                          const hasDefaultCategory = !!defaultBarcode;
                          
                          return (
                            <TableRow key={`${product._id}-${variant._id}`} hover>
                              <TableCell>{product.name}</TableCell>
                              <TableCell>
                                {Object.entries(variant.attributes || {}).map(([key, value]) => (
                                  <span key={key}>{key}: {value} </span>
                                ))}
                              </TableCell>
                              <TableCell>
                                {variant.barcodes && variant.barcodes.length > 0 ? (
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    {variant.barcodes.map((barcode, index) => (
                                      <Box key={barcode._id || index} sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        fontSize: '0.875rem'
                                      }}>
                                        <span style={{
                                          fontFamily: 'monospace',
                                          backgroundColor: '#f5f5f5',
                                          padding: '2px 6px',
                                          borderRadius: '4px',
                                          border: '1px solid #ddd'
                                        }}>
                                          {barcode.code}
                                        </span>
                                        {barcode.isDefault && (
                                          <span style={{
                                            color: '#1976d2',
                                            fontSize: '12px',
                                            fontWeight: 'bold'
                                          }}>
                                            (افتراضي)
                                          </span>
                                        )}
                                        {barcode.defaultCategory === categoryId && (
                                          <span style={{
                                            color: '#ffd700',
                                            fontSize: '14px'
                                          }}>
                                            ★
                                          </span>
                                        )}
                                      </Box>
                                    ))}
                                  </Box>
                                ) : (
                                  <span style={{ color: '#999', fontStyle: 'italic' }}>
                                    لا يوجد باركود
                                  </span>
                                )}
                              </TableCell>
                              <TableCell>{variant.unit}</TableCell>
                              <TableCell>
                                {variant.status === 'active' ? 'نشط' :
                                 variant.status === 'inactive' ? 'غير نشط' : 'نفذت الكمية'}
                              </TableCell>
                              <TableCell align="center">
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                  {hasDefaultCategory ? (
                                    <Button
                                      size="small"
                                      variant="contained"
                                      onClick={() => handleRemoveDefaultCategory(product._id!, variant._id!)}
                                      sx={{
                                        minWidth: 'auto',
                                        backgroundColor: '#ffd700',
                                        color: '#000',
                                        '&:hover': {
                                          backgroundColor: '#ffed4e'
                                        }
                                      }}
                                      title="انقر لإلغاء التصنيف الافتراضي"
                                    >
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <span style={{ fontSize: '16px' }}>★</span>
                                        <span style={{ fontSize: '12px', fontWeight: 'bold' }}>افتراضي</span>
                                      </Box>
                                    </Button>
                                  ) : (
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={() => handleSetAsDefaultCategory(product._id!, variant._id!)}
                                      sx={{
                                        minWidth: 'auto',
                                        color: '#ccc',
                                        borderColor: '#ccc',
                                        '&:hover': {
                                          color: '#ffd700',
                                          borderColor: '#ffd700'
                                        }
                                      }}
                                      title="انقر لتعيين كتصنيف افتراضي"
                                    >
                                      ★
                                    </Button>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell align="center">
                                <IconButton
                                  color="primary"
                                  size="small"
                                  onClick={() => navigate(`/products/edit/${product._id}`)}
                                  title="تعديل المنتج"
                                >
                                  <EditIcon />
                                </IconButton>
                                <IconButton
                                  color="error"
                                  size="small"
                                  onClick={() => handleConfirmRemove(product._id!, variant._id!)}
                                  title="حذف من التصنيف"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                    })}
                   </TableBody>
                 </Table>
               </TableContainer>
             )}
             </>
           )}
           
           {/* مكون الصفحات */}
           {currentTab === 0 && products.length > 0 && getTotalPages() > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Stack spacing={2}>
                <Pagination
                  count={getTotalPages()}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  size="large"
                  showFirstButton
                  showLastButton
                />
                <Typography variant="body2" align="center" color="textSecondary">
                  عرض {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, getPaginatedProducts().length + ((currentPage - 1) * itemsPerPage))} من {products.flatMap(product =>
                    product.variants.filter(variant =>
                      // البحث في تصنيفات المتغير
                      (variant.categories?.includes(categoryId!)) ||
                      // البحث في تصنيفات الباركودات
                      (variant.barcodes && variant.barcodes.some(barcode =>
                        barcode.categories && barcode.categories.includes(categoryId!)
                      ))
                    )
                  ).length} منتج
                </Typography>
              </Stack>
            </Box>
          )}

          {/* Vista de productos disponibles para agregar */}
          {currentTab === 1 && (
            <>
              {/* فلتر البحث */}
              <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
                <TextField
                  label="بحث عن منتج"
                  variant="outlined"
                  size="small"
                  value={availableSearchInput}
                  onChange={(e) => setAvailableSearchInput(e.target.value)}
                  onKeyDown={handleAvailableSearchKeyDown}
                  sx={{ flexGrow: 1 }}
                  placeholder="اكتب اسم المنتج ثم اضغط بحث أو Enter"
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleAvailableSearch}
                  disabled={availableLoading}
                >
                  بحث
                </Button>
                {availableSearchTerm && (
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={handleClearAvailableSearch}
                    disabled={availableLoading}
                  >
                    مسح
                  </Button>
                )}
              </Box>

              {availableLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: 'primary.main' }}>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>اسم المنتج</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>عدد المتغيرات</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>الحالة</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">إجراءات</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {availableProducts.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} align="center">
                            <Typography variant="body1" color="textSecondary" sx={{ py: 3 }}>
                              {availableSearchTerm ? 'لا توجد منتجات تطابق البحث' : 'لا توجد منتجات متاحة'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        availableProducts.map(product => (
                          <TableRow key={product._id} hover>
                            <TableCell>{product.name}</TableCell>
                            <TableCell>{product.variants.length}</TableCell>
                            <TableCell>
                              {product.status === 'active' ? 'نشط' :
                               product.status === 'inactive' ? 'غير نشط' : 'متوقف'}
                            </TableCell>
                            <TableCell align="center">
                              <Button
                                variant="outlined"
                                color="primary"
                                size="small"
                                onClick={() => {
                                  setSelectedProduct(product._id!);
                                  setOpenAddDialog(true);
                                }}
                              >
                                إضافة للتصنيف
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              
              {/* مكون الصفحات للمنتجات المتاحة - server-side */}
              {availableTotalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Stack spacing={2}>
                    <Pagination
                      count={availableTotalPages}
                      page={availableCurrentPage}
                      onChange={handleAvailablePageChange}
                      color="primary"
                      size="large"
                      showFirstButton
                      showLastButton
                    />
                    <Typography variant="body2" align="center" color="textSecondary">
                      عرض {((availableCurrentPage - 1) * availableItemsPerPage) + 1} - {Math.min(availableCurrentPage * availableItemsPerPage, availableTotalItems)} من {availableTotalItems} منتج متاح
                    </Typography>
                  </Stack>
                </Box>
              )}
            </>
          )}
        </>
      )}

      {/* Diálogo para agregar producto a la categoría */}
      <Dialog open={openAddDialog} onClose={handleCloseAddDialog} maxWidth="sm" fullWidth>
        <DialogTitle>إضافة منتج إلى التصنيف</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Selector de producto */}
            <FormControl fullWidth>
              <InputLabel id="product-select-label">المنتج</InputLabel>
              <Select
                labelId="product-select-label"
                value={selectedProduct}
                onChange={handleProductChange}
                label="المنتج"
              >
                <MenuItem value="">
                  <em>اختر المنتج</em>
                </MenuItem>
                {availableProducts.map(product => (
                  <MenuItem key={product._id} value={product._id}>
                    {product.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Selector de variante si se seleccionó un producto */}
            {selectedProduct && (
              <FormControl fullWidth>
                <InputLabel id="variant-select-label">المتغير (اختياري)</InputLabel>
                <Select
                  labelId="variant-select-label"
                  value={selectedVariant}
                  onChange={handleVariantChange}
                  label="المتغير (اختياري)"
                >
                  <MenuItem value="">
                    <em>كل المتغيرات</em>
                  </MenuItem>
                  {availableProducts
                    .find(p => p._id === selectedProduct)
                    ?.variants.map(variant => (
                      <MenuItem key={variant._id} value={variant._id}>
                        {Object.entries(variant.attributes || {}).map(([key, value]) => `${key}: ${value}`).join(', ')}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            )}
            
            {/* خيار التصنيف الافتراضي */}
            {selectedProduct && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isDefaultCategory}
                    onChange={(e) => setIsDefaultCategory(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>تعيين كتصنيف افتراضي</span>
                    <span style={{ color: '#ffd700', fontSize: '16px' }}>★</span>
                  </Box>
                }
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog} color="inherit">
            إلغاء
          </Button>
          <Button 
            onClick={handleAddProductToCategory} 
            variant="contained" 
            color="primary"
            disabled={!selectedProduct}
          >
            إضافة
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo para confirmar eliminar producto de la categoría */}
      <Dialog open={openRemoveDialog} onClose={handleCloseRemoveDialog}>
        <DialogTitle>تأكيد الحذف</DialogTitle>
        <DialogContent>
          <Typography>
            هل أنت متأكد من حذف هذا المنتج من التصنيف؟
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRemoveDialog} color="inherit">
            إلغاء
          </Button>
          <Button 
            onClick={handleRemoveProductFromCategory} 
            variant="contained" 
            color="error"
          >
            حذف
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para notificaciones */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={5000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* مكون الاستيراد المجمع */}
      <CategoryBulkExcelImport
        open={openBulkImport}
        onClose={() => setOpenBulkImport(false)}
        categoryId={categoryId!}
        categoryName={category?.name || ''}
        onImportComplete={() => {
          // تحديث قائمة المنتجات فقط، بدون إغلاق النافذة
          // المستخدم سيغلق النافذة يدوياً بعد مراجعة النتائج
          fetchCategoryProducts();
        }}
      />
    </Box>
  );
};

export default CategoryProducts;